/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Beef, Stethoscope, Heart, CalendarDays, Wheat, BarChart3,
  ChevronLeft, ChevronRight, X, Sparkles,
} from "lucide-react";

const STORAGE_KEY = "mazra3ti_tour_seen_v1";

const STEP_ICONS = [Beef, Stethoscope, Heart, CalendarDays, Wheat, BarChart3];
const STEP_COLORS = [
  { bg: "bg-orange-100", text: "text-[#E05A3D]", accent: "#E05A3D" },
  { bg: "bg-emerald-100", text: "text-[#1C7A3C]", accent: "#1C7A3C" },
  { bg: "bg-pink-100", text: "text-pink-700", accent: "#DB2777" },
  { bg: "bg-amber-100", text: "text-amber-700", accent: "#D97706" },
  { bg: "bg-yellow-100", text: "text-yellow-800", accent: "#A16207" },
  { bg: "bg-purple-100", text: "text-purple-700", accent: "#7E22CE" },
];

export default function WelcomeTour({ open, onClose }) {
  const { t, lang } = useLang();
  const [step, setStep] = useState(0); // 0 = welcome, 1-6 = step
  const totalSteps = t.app.tour.steps.length;
  const isRtl = lang === "ar";

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_e) { /* noop */ }
    setStep(0);
    onClose?.();
  };

  if (!open) return null;

  const isWelcome = step === 0;
  const stepIdx = step - 1;
  const stepData = !isWelcome ? t.app.tour.steps[stepIdx] : null;
  const Icon = !isWelcome ? STEP_ICONS[stepIdx] : Sparkles;
  const colors = !isWelcome ? STEP_COLORS[stepIdx] : { bg: "bg-[#E05A3D]/15", text: "text-[#E05A3D]", accent: "#E05A3D" };

  const Prev = isRtl ? ChevronRight : ChevronLeft;
  const Next = isRtl ? ChevronLeft : ChevronRight;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-lg rounded-3xl p-0 overflow-hidden border-0" data-testid="welcome-tour">
        <button
          onClick={close}
          data-testid="tour-close"
          className="absolute top-4 end-4 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-white grid place-items-center text-[#5C6B5C]"
          aria-label={t.app.tour.skip}
        >
          <X size={18} />
        </button>

        <div className={`${colors.bg} p-8 pb-6 text-center`}>
          <div className="w-20 h-20 rounded-2xl bg-white grid place-items-center mx-auto" style={{ color: colors.accent }}>
            <Icon size={40} />
          </div>
        </div>

        <div className="p-7 text-center">
          {!isWelcome && (
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C6B5C] mb-2">
              {t.app.tour.step_label.replace("{n}", step).replace("{total}", totalSteps)}
            </div>
          )}
          <h2 className="font-display text-2xl md:text-3xl font-black text-[#1C3A27]">
            {isWelcome ? t.app.tour.welcome_title : stepData.title}
          </h2>
          <p className="mt-3 text-[#5C6B5C] leading-relaxed">
            {isWelcome ? t.app.tour.welcome_desc : stepData.desc}
          </p>

          {/* Progress dots */}
          {!isWelcome && (
            <div className="mt-6 flex justify-center gap-1.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${i === stepIdx ? "w-8 bg-[#E05A3D]" : "w-1.5 bg-[#E2E4DF]"}`}
                />
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="mt-7 flex items-center justify-between gap-3">
            {!isWelcome ? (
              <Button
                onClick={() => setStep((s) => s - 1)}
                data-testid="tour-prev"
                variant="outline"
                className="h-11 px-4 rounded-xl border-[#E2E4DF] text-[#5C6B5C]"
              >
                <Prev size={16} className="me-1" /> {t.app.tour.previous}
              </Button>
            ) : (
              <button
                onClick={close}
                data-testid="tour-skip"
                className="text-sm font-semibold text-[#5C6B5C] hover:text-[#1C3A27]"
              >
                {t.app.tour.skip}
              </button>
            )}
            {step < totalSteps ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                data-testid="tour-next"
                className="h-11 px-5 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white font-semibold"
              >
                {t.app.tour.next} <Next size={16} className="ms-1" />
              </Button>
            ) : (
              <Button
                onClick={close}
                data-testid="tour-finish"
                className="h-11 px-5 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold"
              >
                {t.app.tour.finish} <Sparkles size={16} className="ms-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useTourAutoOpen(active) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!active) return;
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) setOpen(true);
    } catch (_e) { /* noop */ }
  }, [active]);
  return [open, setOpen];
}

export function resetTour() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* noop */ }
}
